import { Injectable } from '@angular/core';
import { NgControl } from '@angular/forms';
import { InputMask } from 'primeng/inputmask';
import { Subject } from 'rxjs';
import SimpleKeyboard from 'simple-keyboard';
import { KeyboardOptions } from 'simple-keyboard/build/interfaces';

@Injectable()
export class KeyboardService {
    /**
     * Emits when the visibility of the keyboard changes.
     *
     * true indicates the keyboard became visible.
     */
    public keyboardVisible: Subject<boolean> = new Subject();

    /**
     * Emits when the enter button is clicked.
     */
    public enterClicked: Subject<void> = new Subject();

    /**
     * The virtual keyboard instance.
     */
    private keyboard: SimpleKeyboard;

    /**
     * The form control attached to the keyboard, if there is one.
     */
    private attachedControl: NgControl | null;

    /**
     * Whether or not this keyboard in typing into a masked input.
     */
    private isInputMask = false;

    /**
     * Whether not the keyboard is currently shifted (shift pressed).
     */
    private isShift = false;

    /**
     * Handler for when the keyboard is focued.
     */
    private onFocus(_: Event): void {
        this.keyboard.clearInput();
    }

    /**
     * Handler for when the input changes.
     *
     * @param input The new value of the form input.
     */
    private onChange(input: string): void {
        if (this.attachedControl) {
            this.attachedControl.control?.setValue(input);
        }
    }

    /**
     * Handler for when a key is pressed.
     *
     * @param button The button that was pressed.
     */
    private onKeyPress(button: string): void {
        if (button === '{shift}') {
            this.isShift = !this.isShift;
            this.handleShift();
        } else if (this.isShift && button !== '{backspace}') {
            this.isShift = false;
            this.handleShift();
        }

        if (button === '{numbers}' || button === '{abc}') {
            this.handleNumbers();
        }

        if (button === '{backspace}' && this.isInputMask) {
            this.keyboard.clearInput();
        }

        if (button === '{ent}') {
            this.enterClicked.next();
            this.hide();
        }
    }

    /**
     * Handles shifting the characters displayed in the keyboard.
     */
    private handleShift(): void {
        this.keyboard.setOptions({
            layoutName: this.keyboard.options.layoutName === 'default' ? 'shift' : 'default'
        });
    }

    /**
     * Changes the keyboard to display numbers and symbols.
     */
    private handleNumbers(): void {
        this.keyboard.setOptions({
            layoutName: this.keyboard.options.layoutName !== 'numbersFull' ? 'numbersFull' : 'default'
        });
    }

    /**
     * Attaches a form control to the keyboard.
     *
     * @param ngControl The control to attach.
     * @param maxLength The max length of the input.
     * @param layoutName The name of the layout to use.
     */
    public attachControl(ngControl: NgControl, maxLength: number = 0, layoutName: string = 'default'): void {
        if (!this.keyboard) {
            this.initialize();
        }

        this.attachedControl = ngControl;
        let value: string | null = this.attachedControl.value;

        if (this.attachedControl.valueAccessor instanceof InputMask) {
            const ctrl = this.attachedControl.valueAccessor as InputMask;
            value = ctrl.getUnmaskedValue();
            this.isInputMask = true;
        } else {
            this.isInputMask = false;
        }

        const inputName = String(this.attachedControl.name);
        const keyboardOptions: KeyboardOptions = {
            inputName: inputName,
            maxLength: { [inputName]: maxLength },
            layoutName: layoutName
        };

        this.keyboard.setOptions(keyboardOptions);
        this.keyboard.setInput(value || '', inputName);
        this.keyboardVisible.next(true);
    }

    /**
     * Hides the keyboard.
     */
    public hide(): void {
        this.attachedControl = null;

        if (this.keyboard) {
            this.keyboard.destroy();
            this.keyboard = null;
        }

        this.keyboardVisible.next(false);
    }

    /**
     * Initializes the keyboard.
     */
    public initialize(): void {
        if (this.keyboard) {
            this.keyboard.destroy();
        }

        this.keyboard = new SimpleKeyboard({
            autofocus: true,
            onKeyPress: button => this.onKeyPress(button),
            onChange: input => this.onChange(input),
            onfocus: event => this.onFocus(event),
            layout: {
                default: [
                    'q w e r t y u i o p',
                    'a s d f g h j k l',
                    '{shift} z x c v b n m {backspace}',
                    '{numbers} {space} {ent}'
                ],
                shift: [
                    'Q W E R T Y U I O P',
                    'A S D F G H J K L',
                    '{shift} Z X C V B N M {backspace}',
                    '{numbers} {space} {ent}'
                ],
                numbers: ['1 2 3', '4 5 6', '7 8 9', '{backspace} 0 {ent}'],
                numbersFull: [
                    '1 2 3 4 5 6 7 8 9 0',
                    '- / : ; ( ) $ & @ \'',
                    '. , ? ! \' {backspace}',
                    '{abc} {space} {ent}'
                ],
                alphaNumeric: [
                    '1 2 3 4 5 6 7 8 9 0',
                    'Q W E R T Y U I O P',
                    'A S D F G H J K L',
                    'Z X C V B N M {backspace}',
                    '{ent}'
                ],
            },
            display: {
                '{numbers}': '123',
                '{ent}': 'Enter',
                '{escape}': 'esc ⎋',
                '{tab}': 'tab ⇥',
                '{backspace}': '⌫',
                '{capslock}': 'caps lock ⇪',
                '{shift}': 'Shift',
                '{controlleft}': 'ctrl ⌃',
                '{controlright}': 'ctrl ⌃',
                '{altleft}': 'alt ⌥',
                '{altright}': 'alt ⌥',
                '{metaleft}': 'cmd ⌘',
                '{metaright}': 'cmd ⌘',
                '{abc}': 'ABC',
                '{space}': ' '
            },
            buttonTheme: [
                {
                    class: 'submit-button',
                    buttons: '{ent}'
                }
            ]
        });
    }
}
